import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * The submenu element of the currently active item (may be the overflow menu).
   * @type {HTMLElement | null}
   */
  #activeSubmenu = null;

  /**
   * Whether the active item lives in the default (non-overflow) slot.
   * @type {boolean}
   */
  #activeIsDefaultSlot = true;

  /**
   * Whether the active item has its own submenu.
   * @type {boolean}
   */
  #activeHasSubmenu = false;

  /**
   * Keeps the open submenu's measured height in sync with its real rendered height.
   *
   * The submenu is `content-visibility: auto` with a `contain-intrinsic-size`
   * placeholder, so a one-shot `offsetHeight` read taken in the same tick the menu
   * opens is unreliable: it can return the placeholder size or `0` before the real
   * content has laid out. That race is what made the menu sometimes fail to open,
   * open empty, or open to the wrong height. Observing the active submenu instead
   * means we always react to its true size, however late it settles.
   *
   * @type {ResizeObserver}
   */
  #submenuResizeObserver = new ResizeObserver(() => this.#applyActiveHeight());

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Safety net: when the pointer or keyboard focus leaves the menu entirely, force
    // it shut. The submenu and overflow panels are descendants (including shadow DOM)
    // of this element, so these only fire on a genuine exit, never while the user is
    // still inside an open panel. This is what guarantees the menu can't get stuck open.
    this.addEventListener('pointerleave', this.#handleLeave);
    this.addEventListener('focusout', this.#handleLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerleave', this.#handleLeave);
    this.removeEventListener('focusout', this.#handleLeave);
    this.#submenuResizeObserver.disconnect();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * Close the menu when the pointer or keyboard focus leaves it completely.
   * @param {PointerEvent | FocusEvent} event
   */
  #handleLeave = (event) => {
    // Ignore moves that stay within the menu (e.g. onto an open submenu panel).
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#close();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Remember what's open so #applyActiveHeight (and the ResizeObserver) can
    // recompute the height at any time without re-deriving it from an event.
    this.#activeIsDefaultSlot = isDefaultSlot;
    this.#activeHasSubmenu = hasSubmenu;
    this.#setActiveSubmenu(submenu);

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
    }

    this.style.setProperty('--submenu-opacity', '1');

    // Apply the height now for the common case where content is already laid out.
    // If the measurement is still settling (content-visibility / deferred hydration),
    // the ResizeObserver attached above will fire and correct it. Both `--submenu-height`
    // and `--full-open-header-height` are always written together by #applyActiveHeight,
    // so the panel and its clip-path can never disagree (no empty, half-open box).
    this.#applyActiveHeight();
  };

  /**
   * Point the ResizeObserver at the currently active submenu, detaching the previous one.
   * @param {HTMLElement | null} submenu
   */
  #setActiveSubmenu(submenu) {
    if (this.#activeSubmenu === submenu) return;

    if (this.#activeSubmenu) {
      this.#submenuResizeObserver.unobserve(this.#activeSubmenu);
    }

    this.#activeSubmenu = submenu;

    if (submenu) {
      this.#submenuResizeObserver.observe(submenu);
    }
  }

  /**
   * Compute and apply the open height for the currently active submenu.
   *
   * No-op unless a menu item is active, so a late ResizeObserver callback can never
   * re-open a menu that has already closed (the previous cause of the stuck, empty
   * header box). Writes both height custom properties together.
   */
  #applyActiveHeight = () => {
    const submenu = this.#activeSubmenu;
    const headerComponent = this.headerComponent;

    if (!submenu || !this.#state.activeItem || !headerComponent) return;

    let finalHeight = submenu.offsetHeight || 0;

    // For the overflow menu, the height needs to be either content of the submenu or the total
    // height of the menu list links.
    if (!this.#activeIsDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (this.#activeHasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
  };

  /**
   * Deactivate the active item when the pointer leaves it.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // The pointer (or focus) is still inside the menu. Opening the panel grows the
    // header, and that layout shift can slide a child element under a stationary
    // cursor, firing a spurious `pointerleave` whose relatedTarget is still within the
    // menu (e.g. the item's own label span). Treat anything that stays inside the menu
    // as "not a leave" and let #handleLeave close on the real exit. Without this,
    // re-opening the menu after leaving it opens the panel and then instantly closes it.
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item, unless a related panel is still hovered.
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.#close();
  };

  /**
   * Close the menu unconditionally and reset every piece of open-state tracking.
   *
   * This is the single source of truth for "closed". Every close path funnels through
   * here so the JS state, the aria attributes, the height variables and the
   * ResizeObserver can never drift apart, which is what previously left the panel
   * open (and sometimes empty) after the pointer had moved away.
   */
  #close = () => {
    const item = this.#state.activeItem;
    const submenu = this.#activeSubmenu;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#setActiveSubmenu(null);
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';

    if (item) item.ariaExpanded = 'false';

    // Remove active state from the submenu so it can be render-skipped again.
    if (submenu) delete submenu.dataset.active;
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}